import { ReactNode, useCallback, useEffect } from 'react';
import Spinner from '../spinner/Spinner';

// Usually will define styles in a comment but it is much easier in this format for the button
const styles = {
    base: 'focus:outline-none focus:ring-2 disabled:cursor-default disabled:opacity-50',
    type: {
        primary:
            'border-transparent font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:hover:bg-indigo-600 border shadow-sm',
        danger: ' border-transparent font-medium text-white bg-red-600 hover:bg-red-700 disabled:hover:bg-red-600 border shadow-sm',
        secondary:
            'border-gray-300 font-medium dark:text-white/[0.87] text-gray-700 bg-white dark:bg-white/[0.12] dark:hover:bg-white/[0.09] hover:bg-gray-50 dark:disabled:hover:bg-white/[0.12] disabled:hover:bg-white border dark:border-white/[0.12] shadow-sm',
        hollow: 'font-medium text-gray-700 disabled:hover:bg-white',
    },
    focus: {
        primary: 'focus:ring-indigo-500',
        danger: 'focus:ring-red-500',
        secondary: 'focus:ring-indigo-500',
        hollow: 'focus:ring-indigo-500',
    },
    size: {
        xs: 'px-2 py-1.5 text-xs',
        sm: 'px-2 py-2 text-sm leading-4',
        md: 'px-2 py-2 text-sm',
        lg: 'px-4 py-2 text-base',
        xl: 'px-6 py-3 text-base',
    },
    rounding: {
        xs: 'rounded',
        sm: 'rounded-md',
        md: 'rounded-md',
        lg: 'rounded-md',
        xl: 'rounded-md',
    },
    icon: {
        xs: { start: '-ml-0.5 -mr-2', end: '-mr-0.5 -ml-2', dimensions: 'h-4 w-4' },
        sm: { start: '-ml-0.5 -mr-2', end: '-mr-0.5 -ml-2', dimensions: 'h-4 w-4' },
        md: { start: '-ml-1 -mr-2', end: '-mr-0.5 -ml-2', dimensions: 'h-5 w-5' },
        lg: { start: '-ml-1 -mr-1', end: '-mr-0.5 -ml-2', dimensions: 'h-5 w-5' },
        xl: { start: '-ml-1 -mr-1', end: '-mr-0.5 -ml-2', dimensions: 'h-5 w-5' },
    },
    shortcutKey: {
        primary: 'text-white bg-indigo-500',
        secondary: 'border border-gray-200 text-gray-400',
        danger: 'text-white bg-red-500',
        hollow: 'border border-gray-200 text-gray-400',
    },
};

export type ButtonProps = {
    /**
     * The overall look of the button
     */
    type?: 'primary' | 'danger' | 'secondary' | 'hollow';

    /**
     * What the button should say
     */
    label?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    disabled?: boolean;
    fill?: boolean;
    formId?: string;
    onClick?: () => void;
    isLoading?: boolean;
    isSubmit?: boolean;
    shortcutKey?: string;
    inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
    requiresAltKey?: boolean;
};

function Button({
    type = 'primary',
    onClick,
    label = '',
    size = 'md',
    leftIcon,
    rightIcon,
    formId,
    disabled = false,
    fill = false,
    isLoading = false,
    isSubmit = false,
    shortcutKey,
    inputRef,
    requiresAltKey = false,
}: ButtonProps): JSX.Element {
    // if (size === 'xs' && !label) {
    //     throw new Error('An xs button cannot have an icon and must contain a label.');
    // }
    // if (rightIcon && shortcutKey) {
    //     throw new Error('A button cannot have both right icon and shortcut key at the same time.');
    // }

    const iconDimensions = styles.icon[size].dimensions;

    const detectHotKey = useCallback(
        (e: KeyboardEvent) => {
            const keyLookups: { [key: string]: string } = {
                BracketLeft: 'key[',
                BracketRight: 'key]',
            };

            const _eventKey = keyLookups[e.code] || e.code.toLowerCase();
            if (
                _eventKey === `key${shortcutKey.toLowerCase()}` &&
                !['[object HTMLTextAreaElement]', '[object HTMLInputElement]'].includes(e.target.toString())
            ) {
                if (requiresAltKey) {
                    if (e.altKey) {
                        e.preventDefault();
                        handleOnClick();
                    }
                } else {
                    e.preventDefault();
                    handleOnClick();
                }
            }
        },
        [shortcutKey]
    );

    useEffect(() => {
        if (shortcutKey) {
            document.addEventListener('keydown', detectHotKey);
            return (): void => document.removeEventListener('keydown', detectHotKey);
        }
    }, []);

    function handleOnClick(): void {
        onClick && onClick();
    }

    return (
        <button
            onClick={handleOnClick}
            type={isSubmit ? 'submit' : 'button'}
            form={formId}
            className={`${styles.base} ${styles.focus[type]} ${styles.rounding[size]} ${fill ? 'w-full' : 'w-fit'}`}
            disabled={disabled}
        >
            <span className={`flex justify-center items-center gap-4 ${styles.type[type]} ${styles.size[size]} ${styles.rounding[size]}`}>
                {isLoading && <Spinner size="sm" />}
                {leftIcon && <span className={`${iconDimensions} ${label ? styles.icon[size].start : ''}`}>{leftIcon}</span>}
                {label}
                {rightIcon && <span className={`${iconDimensions} ${label ? styles.icon[size].end : ''}`}>{rightIcon}</span>}
                {shortcutKey && (
                    <kbd className={`rounded px-1 py-0.5 text-xs font-sans -ml-1 ${styles.shortcutKey[type]}`}>
                        {requiresAltKey && '⌥ '} {shortcutKey.toUpperCase()}
                    </kbd>
                )}
            </span>
        </button>
    );
}

export default Button;
